import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';


const AdminSidebar = ({ isCollapsed = false, onToggle, activeTab, onTabChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { restaurantId: authRestaurantId, linkedRestaurantId, profileLoading } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Keep restaurant context on QR Codes / editor so restaurant admins aren't sent to access-denied.
  // Prefer URL param, then linked profile id, then auth/tenant-resolved id (after profile loads).
  const restaurantIdParam =
    searchParams?.get('restaurant_id') || linkedRestaurantId || authRestaurantId;
  const withRestaurantId = (path) =>
    restaurantIdParam ? `${path}?restaurant_id=${restaurantIdParam}` : path;

  const navigationItems = [
    {
      path: '/restaurant-admin-dashboard',
      label: 'Dashboard',
      icon: 'LayoutDashboard',
      tab: 'dashboard',
    },
    {
      path: '/qr-code-generator',
      label: 'QR Codes',
      icon: 'QrCode',
      tab: null,
    },
    {
      path: '/management-content-editor',
      label: 'Éditeur de Contenu',
      icon: 'Edit3',
      tab: null,
    },
    {
      path: '/restaurant-admin-dashboard',
      label: 'Paiement',
      icon: 'CreditCard',
      tab: 'payment',
    },
  ];

  const isActiveRoute = (item) => {
    if (item?.tab) {
      return location?.pathname === item?.path && activeTab === item?.tab;
    }
    return location?.pathname === item?.path;
  };

  const isDashboardPath =
    location?.pathname === '/restaurant-admin-dashboard' || location?.pathname === '/dashboard';

  const handleNavClick = (item, e) => {
    // Avoid opening QR / editor before restaurant context is ready (causes access-denied).
    if (!item?.tab && profileLoading && !restaurantIdParam) {
      e?.preventDefault();
      return;
    }
    if (item?.tab === 'payment') {
      e?.preventDefault();
      // If already on the dashboard, just switch the tab
      if (isDashboardPath) {
        if (onTabChange) onTabChange('payment');
      } else {
        // Navigate to dashboard with query param so it opens on payment tab
        const base = withRestaurantId('/restaurant-admin-dashboard');
        const sep = base?.includes('?') ? '&' : '?';
        navigate(`${base}${sep}tab=payment`);
      }
    } else if (item?.tab) {
      // Same-page tab switch only — let <Link> handle cross-page nav so restaurant_id is kept
      if (isDashboardPath && onTabChange) {
        e?.preventDefault();
        onTabChange(item?.tab);
      }
    }
    closeMobileMenu();
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileOpen(false);
  };

  return (
    <>
      <button
        onClick={toggleMobileMenu}
        className="lg:hidden fixed top-4 left-4 z-nav bg-card text-foreground p-3 rounded-md shadow-warm-md touch-target transition-smooth hover:shadow-warm-lg active:scale-96"
        aria-label="Toggle mobile menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} />
      </button>
      <aside
        className={`admin-sidebar fixed lg:fixed top-0 left-0 h-full bg-card border-r border-border shadow-warm-lg transition-smooth z-nav ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${isCollapsed ? 'w-20' : 'w-64'}`}
      >
        <div className="admin-sidebar-header flex items-center justify-between h-20 px-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="admin-logo-container bg-primary/10 rounded-lg p-2 transition-smooth">
              <Icon 
                name="Utensils" 
                size={isCollapsed ? 24 : 32} 
                color="var(--color-primary)" 
                className="admin-logo transition-smooth"
              />
            </div>
            {!isCollapsed && (
              <div className="admin-brand-text">
                <h2 className="text-lg font-heading font-semibold text-foreground">
                  QR Menu AR
                </h2>
                <p className="text-xs font-caption text-muted-foreground">
                  Admin Panel
                </p>
              </div>
            )}
          </div>
          <button
            onClick={onToggle}
            className="hidden lg:block text-muted-foreground hover:text-foreground transition-smooth"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
          </button>
        </div>

        <nav className="admin-sidebar-nav p-4 space-y-2">
          {navigationItems?.map((item) => {
            const isActive = isActiveRoute(item);
            return (
              <Link
                key={`${item?.path}-${item?.tab || 'main'}`}
                to={item?.tab === 'payment' ? '#' : withRestaurantId(item?.path)}
                onClick={(e) => handleNavClick(item, e)}
                className={`admin-nav-item flex items-center space-x-3 px-4 py-3 rounded-lg transition-smooth touch-target ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-warm'
                    : 'text-foreground hover:bg-muted hover:shadow-warm-sm active:scale-96'
                } ${isCollapsed ? 'justify-center' : ''}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon name={item?.icon} size={20} />
                {!isCollapsed && (
                  <span className="font-body font-medium">{item?.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer absolute bottom-0 left-0 right-0 p-4 border-t border-border">
          {!isCollapsed && (
            <div className="text-center">
              <p className="text-sm font-bold text-foreground tracking-wide">SCANN3R</p>
              <p className="text-xs text-muted-foreground mt-0.5">819-989-1310</p>
              <p className="text-xs text-muted-foreground mt-0.5">felix.gagnon@scann3r.ca</p>
            </div>
          )}
        </div>
      </aside>
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-background z-dropdown"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default AdminSidebar;